import React, { useState, useEffect } from 'react'
import { LoaderFunction, redirect, useLocation } from 'react-router-dom'
import { store } from '../../redux/store'
import { FetchProfile } from 'shared/redux/profile/ProfileSlice'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import {
  Box,
  BoxProps,
  Container,
  Grid,
  Tab,
  Tabs,
  useMediaQuery,
} from '@mui/material'
import fast from 'shared/assets/icons/medal.svg'
import Link from '@mui/material/Link'
import Realtime from 'shared/assets/icons/star.svg'
import logo from 'shared/assets/icons/logos/mePrismprivacynew.svg'
import background from '../../assets/images/IndividualCreateBackground.png'
import { ThemeProvider } from '@emotion/react'
import { MpReTheme, fonts, theme } from '../../theme/OnboardingTheme'
import { MOBILE_BREAKPOINT_VAL } from '../../theme/theme'
import icon4 from 'shared/assets/icons/lucide_scan-text.svg'
import icon5 from 'shared/assets/icons/no.svg'
import ConsentAgreement from './ConsentAgreement'
import { useAppSelector, useAppDispatch } from '../../redux/storeExports'
import { selectActiveGrant, fetchGrants } from '../../redux/product/ProductSlice'
import { showModalForGrant, isGrantFromCodeRedemption } from 'shared/redux/product/ProductSlice'
import SessionExpirationModal from '../shared/molecule/SessionExpirationModal'
// eslint-disable-next-line import/extensions
import { useSessionExpiration } from '../../utils/hooks/useSessionExpiration'

// Route-level guard: runs BEFORE the authorization page mounts. Redirects users
// who shouldn't be here so they never see the form.
export const authorizationRouteLoader: LoaderFunction = async () => {
  try {
    await Promise.all([
      store.dispatch(FetchProfile()),
      store.dispatch(fetchGrants()),
    ])
  } catch {
    return null
  }

  const state = store.getState()
  const profile = state.profile?.profile
  const grant = selectActiveGrant(state as any)

  const isOnboardingComplete = profile?.metadata?.onboarding_step === 2

  if (!isOnboardingComplete) return redirect('/onboarding')
  if (!grant || profile?.mp_auth_letter_signature) return redirect('/dashboard')

  return null
}

export type SignInTab = 'create' | 'sign in'
function Item(props: BoxProps) {
  const { ...other } = props
  return <Box {...other} />
}
export const AuthCopy = ({ }: { mode: SignInTab }) => {
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  return (
    <>
      <ThemeProvider theme={theme}>
        {!isMobile && (<Box sx={{ px: 10, pt: 10 }}>
          <img src={logo} alt="Logo" style={{ width: 250, height: 75, objectFit: 'contain' }} />{' '}
        </Box>)}


        <Stack
          sx={{
            px: 10,
            pt: 10,
            pb: 20,
            '& .MuiTypography-root': {
              color: '#FFFFFF',
            },
            justifyContent: 'flex-end',
          }}>
          <Typography
            variant={'h1'}
            sx={{
              my: 2,
              fontWeight: 'bold',
              maxWidth: !isMobile ? `${MOBILE_BREAKPOINT_VAL}px` : 'unset',
            }}>
            Quick guide to your authorization letter
          </Typography>
          <Box
            sx={{
              display: 'flex',
              columnGap: 1,
              rowGap: 1,
              alignItems: 'center',
              gridTemplateColumns: 'auto, 5fr',
              py: 5
            }}>
            <Item>
              <img
                src={fast}
                alt="Logo"
                style={{ marginRight: 8, width: 42, height: 42 }}
              />
            </Item>
            <Item>
              {' '}
              <Typography variant="body2">
                <span style={{ color: '#EFEDFDB2' }}>
                  Data brokers may require written proof before removing your personal information.</span>

              </Typography>{' '}
            </Item>
          </Box>
          <Box
            sx={{
              display: 'flex',
              columnGap: 1,
              rowGap: 1,
              alignItems: 'center',
              gridTemplateColumns: 'auto, 1fr',
              py: 5
            }}>
            <Item>
              <img
                src={Realtime}
                alt="Logo"
                style={{ marginRight: 8, width: 42, height: 42 }}
              />
            </Item>
            <Item sx={{ gridColumn: 'span 5' }}>
              <Typography variant="body2">
                <span style={{ color: '#EFEDFDB2' }}>This letter verifies that Priwall acts on your behalf in such situations.
                </span>


              </Typography>{' '}
            </Item>
          </Box>
          <Box
            sx={{
              display: 'flex',
              columnGap: 1,
              rowGap: 1,
              alignItems: 'center',
              gridTemplateColumns: 'auto, 1fr',
              py: 5
            }}>
            <Item>
              <img
                src={Realtime}
                alt="Logo"
                style={{ marginRight: 8, width: 42, height: 42 }}
              />
            </Item>
            <Item sx={{ gridColumn: 'span 5' }}>
              <Typography variant="body2">
                <span style={{ color: '#EFEDFDB2', marginRight: '5px' }}>This letter is solely for removing your information from the web and carries no other legal implications.
                </span>

              </Typography>{' '}
            </Item>
          </Box>
          <Box
            sx={{
              alignItems: 'left',
              marginTop: 4,
              p: 10,
              backgroundColor: 'rgba(255, 255, 255, 0.15)',
              border:
                '2px solid var(--text-colors-stroke, rgba(255, 255, 255, 0.15))',
              borderRadius: '20px',
              backdropFilter: 'blur(20.5px)',
            }}>
            <Typography variant="h4">
              Information <span style={{ color: '#C67EFF' }}>Security</span>
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexDirection: ['column', 'row'],
                justifyContent: 'space-between',
                marginTop: 4,
              }}>
              {/* Item 1 */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'flex-start',
                }}>
                <img
                  src={icon4}
                  alt="Icon2"
                  style={{
                    verticalAlign: 'middle',
                    marginRight: 8,
                    width: 24,
                    height: 24,
                  }}
                />
                <Typography variant="body3">
                  Priwall is SOC 2 compliant with industry-grade cybersecurity standards for handling sensitive data
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'flex-start',
                  marginTop: isMobile ? 4 : 'unset'
                }}>
                <img
                  src={icon5}
                  alt="Icon2"
                  style={{
                    verticalAlign: 'middle',
                    marginRight: 8,
                    width: 24,
                    height: 24,
                    marginLeft: 5,
                  }}
                />
                <Typography variant="body3">
                  We are a privacy agent. We will never share or sell the information you provide to us. All the data you provide during onboarding is used for the sole expressed purpose of finding and removing your information.
                </Typography>
              </Box>
            </Box>
          </Box>
        </Stack>
      </ThemeProvider>
    </>
  )
}

export const AuthorizationAgentWrapper = () => {
  const location = useLocation()
  const textStyle = {
    ...fonts.Inter.Regular,
    fontSize: '14px',
  }
  const mode: SignInTab = location.pathname.includes('create')
    ? 'create'
    : 'sign in'
  const isWide = useMediaQuery(theme.breakpoints.up('sm'))
  const [open, setOpen] = useState(false)
  const [name, setName] = useState('')
  const dispatch = useAppDispatch()

  // Get the grant value from Redux store
  const grant = useAppSelector(selectActiveGrant)

  // Add a loading state to track when grants are being fetched
  const [isLoading, setIsLoading] = useState(true)
  const { isSessionExpirationModalOpen, closeSessionExpirationModal } = useSessionExpiration()
  // Fetch grants when component mounts
  useEffect(() => {
    const fetchGrantData = async () => {
      try {
        await dispatch(fetchGrants());
      } catch (error) {
        console.error('Failed to fetch grants:', error);
      } finally {
        setIsLoading(false)
        console.log('AuthorizationAgentWrapper - Finished fetching grants, setting isLoading to false')
      }
    };
    fetchGrantData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Use the helper function to determine if we should show the grant tabs
  const showGrantTabs = showModalForGrant(grant);

  // Determine if the grant is from code redemption
  const isFromCodeRedemption = isGrantFromCodeRedemption(grant);

  // Determine if we should show the modal based on grant source and code redemption
  const shouldShowModal = showGrantTabs && !isFromCodeRedemption;

  return (
    <ThemeProvider theme={theme}>
      <Container maxWidth={false} sx={{ background: '#1A1B20', minHeight: '100vh' }}>
        <Grid container>
          <Grid item xs={12} md={5} sx={{ minHeight: { md: '100vh' }, display: { md: 'flex' }, flexDirection: { md: 'column' } }}>
            <Box sx={{ flex: { md: 1 } }}>
              {shouldShowModal && !isLoading && (
                !isWide ? (<Tabs
                  sx={{ mx: 7, pt: 10, ".MuiTab-root": { alignItems: "start", }, }}
                  value={3}
                  indicatorColor="secondary"
                  textColor="primary"
                  centered>
                  <Tab
                    style={{ minWidth: '20%' }}
                    label="1."
                    sx={{
                      margin: '0 3px',
                      textTransform: 'none',
                      fontFamily: fonts.Inter.SemiBold,
                    }}
                  />
                  <Tab
                    label="2."
                    style={{ minWidth: '20%' }}
                    sx={{
                      margin: '0 3px',
                      textTransform: 'none',
                      fontFamily: fonts.Inter.SemiBold,
                    }}
                  />
                  <Tab
                    label="3."
                    style={{ minWidth: '20%' }}
                    sx={{
                      margin: '0 3px',
                      textTransform: 'none',
                      fontFamily: fonts.Inter.SemiBold,
                    }}
                  />
                  <Tab
                    label="4.Authorization"
                    sx={{
                      margin: '0 3px',
                      width: '40%',
                      textTransform: 'none',
                      fontFamily: fonts.Inter.SemiBold,
                      color: 'inherit',
                      '&.Mui-selected': {
                        color: MpReTheme.colors.tertiary.pink.default,
                      },
                    }}
                  />
                </Tabs>) : (<Tabs
                  sx={{ px: 5, pt: 10, ".MuiTab-root": { alignItems: "start", }, }}
                  value={3}
                  indicatorColor="secondary"
                  textColor="primary"
                  centered>
                  <Tab

                    label="1.Sign Up"
                    sx={{
                      margin: '0 2px',
                      textTransform: 'none',
                      fontFamily: fonts.Inter.SemiBold,
                    }}
                  />
                  <Tab
                    label="2.Verify"
                    sx={{
                      margin: '0 2px',
                      textTransform: 'none',
                      fontFamily: fonts.Inter.SemiBold,
                    }}
                  />
                  <Tab
                    label="3.Set Profile"
                    sx={{
                      margin: '0 2px',
                      textTransform: 'none',
                      fontFamily: fonts.Inter.SemiBold,
                    }}
                  />
                  <Tab
                    label="4.Authorization"
                    sx={{
                      margin: '0 2px',
                      textTransform: 'none',
                      fontFamily: fonts.Inter.SemiBold,
                      color: 'inherit',
                      '&.Mui-selected': {
                        color: MpReTheme.colors.tertiary.pink.default,
                      },
                    }}
                  />
                </Tabs>)
              )}
              <Box sx={{ px: 7, pt: { xs: 12, md: 20 }, pb: { xs: 8, md: 10 } }}>
                <ConsentAgreement
                  open={shouldShowModal ? open : false}
                  setOpen={setOpen}
                  name={name}
                  setName={setName}
                  textInputProps={{ label: '' }}
                  grant={grant}
                />
              </Box>
            </Box>
            <Box sx={{ px: 7, pb: 6, width: '80%' }}>
              <Stack
                direction={'row'}
                sx={{ alignItems: 'center', width: '100%', justifyContent: 'space-between' }}>
                <Link
                  href={'https://meprism.com/terms-conditions'}
                  sx={{
                    ...textStyle,
                    color: MpReTheme.colors.secondary.default,
                  }}>
                  Terms & Conditions
                </Link>
                <Link
                  href={'https://meprism.com/privacy-policy'}
                  sx={{
                    ...textStyle,
                    color: MpReTheme.colors.secondary.default,
                  }}>
                  Privacy Policy
                </Link>
                <Link
                  href={'https://meprism1.atlassian.net/servicedesk/customer/portal/1/group/1/create/17'}
                  sx={{
                    ...textStyle,
                    color: MpReTheme.colors.secondary.default,
                  }}>
                  Contact Us
                </Link>
              </Stack>
            </Box>
          </Grid>
          <Grid
            item
            xs={0}
            md={7}
            sx={{
              backgroundImage: `url(${background})`,
              backgroundRepeat: 'no-repeat',
              backgroundSize: 'cover',
              alignItems: 'flex-end',
              minHeight: { md: '100vh' },
            }}>
            <Box sx={{ position: 'relative', height: '100%', pb: 0 }}>
              <AuthCopy mode={mode} />
              <Typography variant="body4" sx={{ mx: 10, position: 'absolute', bottom: !isWide ? '30px' : '22px' }}>
                Are you a Business?
                <Link
                  href={
                    'https://meprism1.atlassian.net/servicedesk/customer/portal/1/group/1/create/17'
                  }
                  style={{ textDecoration: 'underline', fontSize: '16px', marginLeft: '5px' }}
                  sx={{
                    ...textStyle,
                    color: MpReTheme.colors.tertiary.pink.default,
                    ml: 3
                  }}>
                  Contact us
                </Link>
              </Typography></Box>
          </Grid>
        </Grid>
      </Container>
      {!shouldShowModal && (
        <SessionExpirationModal open={isSessionExpirationModalOpen} onClose={closeSessionExpirationModal} />
      )}
    </ThemeProvider>
  )
}
